import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useState,
} from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import VoiceTyping from "../components/VoiceTyping";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUndo, faRedo, faComment } from "@fortawesome/free-solid-svg-icons";

// The default color for ALL headings is black
const Header = Quill.import("formats/header");
class CustomHeader extends Header {
  static create(value) {
    let node = super.create(value);
    // Ensure all heading levels are black and bold
    node.style.color = "#000000";
    node.style.fontWeight = "bold";
    return node;
  }

  // Override the format method to ensure black color is applied
  static formats(domNode) {
    return domNode.tagName.toLowerCase().substring(1);
  }
}
Quill.register(CustomHeader, true);

// Register fonts
const Font = Quill.import("formats/font");
Font.whitelist = ["sans-serif", "serif", "monospace", "times-new-roman"];
Quill.register(Font, true);

// Register different font sizes
const Size = Quill.import("attributors/style/size");
Size.whitelist = ["10px", "12px", "14px", "16px", "18px", "20px", "24px"];
Quill.register(Size, true);

const TextEditor = forwardRef(
  (
    {
      showToolbarOnly,
      showEditorOnly,
      quillRefs,
      containerRefs,
      isPaginatingRef,
      pages,
      setPages,
      initializeQuill,
      onVoiceTypingResult,
      applyFormatToMultiSelections,
      multiSelections = [],
      /** When false, hides the speech-to-text (VoiceTyping) toolbar control */
      voiceTypingEnabled = true,
      onCommentButtonClick,
      safeShareMode = false,
      safeShareMethod = "mask",
      isDeidentifying = false,
      onToggleSafeShare,
      onSafeShareMethodChange,
    },
    ref
  ) => {
    // State for tracking undo/redo availability
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);

    // Function to update undo/redo button states
    const updateHistoryState = () => {
      const editor = ref?.current?.getEditor(0);
      if (editor && editor.history) {
        setCanUndo(editor.history.stack.undo.length > 0);
        setCanRedo(editor.history.stack.redo.length > 0);
      }
    };

    // Constants for page height and margins
    const PAGE_HEIGHT = 800;
    const PAGE_MARGIN = 24;

    useImperativeHandle(ref, () => ({
      getEditor: (pageIndex = 0) => {
        // Quill mounts asynchronously; callers must handle null until ready.
        if (!quillRefs.current[pageIndex]) {
          return null;
        }
        return quillRefs.current[pageIndex];
      },
      getEditorCount: () => quillRefs.current.length,
      getBoundsForSection: (pageIndex, title) => {
        if (!quillRefs.current[pageIndex]) return [0, 0];
        for (let i = 0; i < quillRefs.current.length; i++) {
          const editor = quillRefs.current[pageIndex];
          const lines = editor.getLines();

          let startIndex = -1;
          let endIndex = editor.getLength();
          let foundHeader = false;

          for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            if (!line?.domNode) continue;

            const lineText = line.domNode.innerText.trim();
            const lineAttributes = editor.getFormat(editor.getIndex(line));

            // If we find the header we're looking for
            if (lineAttributes.header && lineText === title) {
              startIndex = editor.getIndex(line);
              foundHeader = true;
            }

            // If we found the header and now see another header, stop
            else if (foundHeader && lineAttributes.header) {
              endIndex = editor.getIndex(line);
              break;
            }
          }
          // Return the start and end indices
          return [startIndex, endIndex];
        }
      },
    }));

    // Effect to set up history state tracking
    useEffect(() => {
      // Add a small delay to ensure editor is fully initialized
      const timeoutId = setTimeout(() => {
        const editor = ref?.current?.getEditor(0);
        if (!editor) {
          // Editor not ready yet, skip silently
          return;
        }
        if (editor && editor.history) {
          // Initial state check
          updateHistoryState();

          // Listen for text changes to update history state
          const handleTextChange = () => {
            // Use setTimeout to ensure history is updated after the change
            setTimeout(updateHistoryState, 100);
          };

          editor.on("text-change", handleTextChange);

          // Cleanup
          return () => {
            editor.off("text-change", handleTextChange);
          };
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }, [ref?.current]);

    // Effect to intercept keyboard shortcuts for multi-selections
    useEffect(() => {
      const editor = ref?.current?.getEditor(0);
      if (!editor) return;

      const handleKeyDown = (event) => {
        // Only intercept if we have multi-selections and the key combination matches formatting shortcuts
        if (multiSelections.length > 0 && (event.ctrlKey || event.metaKey)) {
          const key = event.key.toLowerCase();
          
          // Check for formatting shortcuts
          if (key === 'b' && applyFormatToMultiSelections) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            applyFormatToMultiSelections({ bold: true });
            return false;
          } else if (key === 'i' && applyFormatToMultiSelections) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            applyFormatToMultiSelections({ italic: true });
            return false;
          } else if (key === 'u' && applyFormatToMultiSelections) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            applyFormatToMultiSelections({ underline: true });
            return false;
          }
        }
      };

      // Attach to the editor's root element and document for better interception
      const editorElement = editor.root;
      if (editorElement) {
        editorElement.addEventListener('keydown', handleKeyDown, true);
      }
      
      // Also attach to document to catch shortcuts before Quill processes them
      document.addEventListener('keydown', handleKeyDown, true);

      return () => {
        if (editorElement) {
          editorElement.removeEventListener('keydown', handleKeyDown, true);
        }
        document.removeEventListener('keydown', handleKeyDown, true);
      };
    }, [ref?.current, multiSelections, applyFormatToMultiSelections]);

    // Effect to intercept toolbar button clicks before Quill processes them
    useEffect(() => {
      if (!showToolbarOnly) return;

      let cleanupFunctions = [];

      // Add a small delay to ensure toolbar is fully rendered
      const timeoutId = setTimeout(() => {
        const toolbar = document.getElementById('toolbar');
        if (!toolbar) return;

        // Find all formatting buttons and attach handlers directly
        const boldButton = toolbar.querySelector('.ql-bold');
        const italicButton = toolbar.querySelector('.ql-italic');
        const underlineButton = toolbar.querySelector('.ql-underline');
        const strikeButton = toolbar.querySelector('.ql-strike');

        const handleButtonClick = (event, format) => {
          // Only intercept if we have multi-selections
          if (multiSelections.length > 0 && applyFormatToMultiSelections) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            applyFormatToMultiSelections(format);
            return false;
          }
          // Otherwise, let Quill handle it normally
        };

        if (boldButton) {
          const handler = (e) => handleButtonClick(e, { bold: true });
          boldButton.addEventListener('click', handler, true);
          cleanupFunctions.push(() => boldButton.removeEventListener('click', handler, true));
        }

        if (italicButton) {
          const handler = (e) => handleButtonClick(e, { italic: true });
          italicButton.addEventListener('click', handler, true);
          cleanupFunctions.push(() => italicButton.removeEventListener('click', handler, true));
        }

        if (underlineButton) {
          const handler = (e) => handleButtonClick(e, { underline: true });
          underlineButton.addEventListener('click', handler, true);
          cleanupFunctions.push(() => underlineButton.removeEventListener('click', handler, true));
        }

        if (strikeButton) {
          const handler = (e) => handleButtonClick(e, { strike: true });
          strikeButton.addEventListener('click', handler, true);
          cleanupFunctions.push(() => strikeButton.removeEventListener('click', handler, true));
        }
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        cleanupFunctions.forEach(cleanup => cleanup());
      };
    }, [showToolbarOnly, multiSelections, applyFormatToMultiSelections]);

    return (
      <div>
        {/* Toolbar */}
        {showToolbarOnly && (
          <div id="toolbar">
            <select className="ql-header" defaultValue="">
              <option value="">Normal</option>
              <option value="1">Heading 1</option>
              <option value="2">Heading 2</option>
              <option value="3">Heading 3</option>
              <option value="4">Heading 4</option>
              <option value="5">Heading 5</option>
            </select>
            <select className="ql-font" defaultValue="times-new-roman">
              <option value="sans-serif">Sans Serif</option>
              <option value="serif">Serif</option>
              <option value="monospace">Monospace</option>
              <option value="times-new-roman">Times New Roman</option>
            </select>
            <select
              className="ql-size"
              defaultValue="14px"
              onChange={(e) => {
                const editor = ref?.current?.getEditor(0);
                if (multiSelections.length > 0 && applyFormatToMultiSelections) {
                  // Apply to all multi-selections
                  applyFormatToMultiSelections({ size: e.target.value });
                } else if (editor) {
                  // Use normal Quill formatting
                  const selection = editor.getSelection();
                  if (selection) {
                    editor.formatText(selection.index, selection.length, 'size', e.target.value, 'user');
                  }
                }
              }}
            >
              <option value="10px">10px</option>
              <option value="12px">12px</option>
              <option value="14px">14px</option>
              <option value="16px">16px</option>
              <option value="18px">18px</option>
              <option value="20px">20px</option>
            </select>
            <button className="ql-bold"></button>
            <button className="ql-italic"></button>
            <button className="ql-underline"></button>
            <button className="ql-strike"></button>
            <button className="ql-list" value="ordered"></button>
            <button className="ql-list" value="bullet"></button>
            <select className="ql-color"></select>
            <select className="ql-background"></select>
            <select className="ql-align"></select>
            <button
              className="ql-comment"
              title="Add Comment"
              type="button"
              onClick={onCommentButtonClick}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "28px",
                height: "24px",
                padding: "3px 5px",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                color: "#444",
                verticalAlign: "middle",
              }}
            >
              <FontAwesomeIcon icon={faComment} />
            </button>
            <button className="ql-image" title="Insert Image"></button>
            <button
              className="ql-undo"
              title="Undo (Ctrl+Z)"
              disabled={!canUndo}
              onClick={() => {
                const editor = ref?.current?.getEditor(0);
                if (editor && canUndo) {
                  editor.history.undo();
                  // Update state after undo
                  setTimeout(updateHistoryState, 100);
                }
              }}
              style={{
                opacity: canUndo ? 1 : 0.5,
                cursor: canUndo ? "pointer" : "default",
                color: canUndo ? "inherit" : "#999",
                pointerEvents: canUndo ? "auto" : "none",
              }}
            >
              <FontAwesomeIcon icon={faUndo} />
            </button>
            <button
              className="ql-redo"
              title="Redo (Ctrl+Y / Cmd+Shift+Z)"
              disabled={!canRedo}
              onClick={() => {
                const editor = ref?.current?.getEditor(0);
                if (editor && canRedo) {
                  editor.history.redo();
                  // Update state after redo
                  setTimeout(updateHistoryState, 100);
                }
              }}
              style={{
                opacity: canRedo ? 1 : 0.5,
                cursor: canRedo ? "pointer" : "default",
                color: canRedo ? "inherit" : "#999",
                pointerEvents: canRedo ? "auto" : "none",
              }}
            >
              <FontAwesomeIcon icon={faRedo} />
            </button>
            <div
              className="ql-voice-typing"
              style={{ display: "inline-block", marginLeft: "8px" }}
            >
              <VoiceTyping
                editorRef={ref}
                onTranscriptionResult={onVoiceTypingResult}
                size="sm"
                isEnabled={voiceTypingEnabled}
              />
            </div>
            
            {/* Safe-Share Mode Controls */}
            <div
              className="ql-safe-share"
              style={{ display: "inline-flex", alignItems: "center", marginLeft: "12px", gap: "4px" }}
            >
              <button
                type="button"
                onClick={onToggleSafeShare}
                disabled={isDeidentifying}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  fontSize: "12px",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  border: safeShareMode ? "1px solid #319795" : "1px solid #cbd5e0",
                  backgroundColor: safeShareMode ? "#319795" : "#edf2f7",
                  color: safeShareMode ? "#fff" : "#4a5568",
                  cursor: isDeidentifying ? "not-allowed" : "pointer",
                  fontWeight: "bold",
                }}
              >
                {isDeidentifying ? "Processing..." : safeShareMode ? "🔒 Safe-Share Active" : "🔓 Safe-Share"}
              </button>
              {!safeShareMode && (
                <select
                  value={safeShareMethod}
                  onChange={(e) => onSafeShareMethodChange?.(e.target.value)}
                  disabled={isDeidentifying}
                  style={{
                    fontSize: "12px",
                    padding: "3px 6px",
                    borderRadius: "4px",
                    border: "1px solid #cbd5e0",
                    backgroundColor: "#fff",
                    color: "#4a5568",
                    outline: "none",
                  }}
                >
                  <option value="mask">Mask</option>
                  <option value="redact">Redact</option>
                  <option value="hash">Hash</option>
                </select>
              )}
            </div>
          </div>
        )}

        {/* Pages */}
        {showEditorOnly && (
          <div
            className="editor-wrapper"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {pages.map((page, index) => (
              <div
                key={index}
                ref={(el) => (containerRefs.current[index] = el)}
                style={{
                  width: "800px",
                  marginBottom: `${PAGE_MARGIN}`,
                  backgroundColor: "#fff",
                  padding: "40px 20px",
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  position: "relative",
                }}
              ></div>
            ))}
          </div>
        )}
      </div>
    );
  }
);

export default TextEditor;
